/**
 * MobileDonut - half-donut anchored to the bottom of the viewport on mobile/tablet.
 * Mirrors the desktop DonutWheel: proportional pizza slices with curved labels.
 * The bottom half is clipped so a semicircle of slices sits above the bottom edge;
 * the user swipes left/right (or scrolls) to rotate. Tap a slice to select.
 */

import { useRef, useState, useCallback, useEffect } from "react";
import type { LucideIcon } from "lucide-react";
import { t, type TranslationKey } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";
import { sectorPath, labelArcPath, polar } from "@/lib/utils/donut";

const BRAND   = "hsl(var(--brand))";
const SIZE    = 360;
const OUTER_R = SIZE / 2;                            // 180
const INNER_R = Math.round(OUTER_R * 0.58);          // ~104
const LABEL_R = Math.round((OUTER_R + INNER_R) / 2); // ~142
const GAP_DEG = 2.6;

interface Tool {
  id: string;
  icon: LucideIcon;
  label: string;
  badge?: string;
  route?: string;
}

interface MobileDonutProps {
  tools: Tool[];
  activeTool: string | null;
  onToolSelect: (id: string) => void;
  onDeselect: () => void;
}

const MobileDonut = ({ tools, activeTool, onToolSelect, onDeselect }: MobileDonutProps) => {
  useLocale(); // re-render labels on language change

  const ringRef      = useRef<SVGGElement>(null);
  const current      = useRef(0);
  const target       = useRef(0);
  const raf          = useRef<number | null>(null);
  const touchX       = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const applyAngle = useCallback((angle: number) => {
    if (ringRef.current) {
      ringRef.current.setAttribute("transform", `rotate(${angle} ${OUTER_R} ${OUTER_R})`);
    }
  }, []);

  const animate = useCallback(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    const tick = () => {
      const diff = target.current - current.current;
      if (Math.abs(diff) < 0.02) {
        current.current = target.current;
        applyAngle(current.current);
        raf.current = null;
        return;
      }
      current.current += diff * 0.12;
      applyAngle(current.current);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, [applyAngle]);

  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    target.current += (e.deltaX || e.deltaY) * 0.18;
    animate();
  }, [animate]);

  const onTouchStart = useCallback((e: TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  }, []);
  const onTouchMove = useCallback((e: TouchEvent) => {
    if (touchX.current === null) return;
    const newX = e.touches[0].clientX;
    const dx = newX - touchX.current;
    touchX.current = newX;
    target.current += dx * 0.6;
    animate();
  }, [animate]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [onWheel, onTouchStart, onTouchMove]);

  useEffect(() => { applyAngle(0); }, [applyAngle]);

  // Auto-center: when a tool becomes active, rotate the half-donut so its slice
  // sits at the top-middle of the visible (top) half - screen angle 270deg.
  useEffect(() => {
    if (!activeTool) return;
    const i = tools.findIndex(tl => tl.id === activeTool);
    if (i < 0) return;
    const stepDeg = 360 / tools.length;
    let next = 360 - (i + 0.5) * stepDeg;          // brings slice i's mid to 270deg
    while (next - current.current > 180)  next -= 360;
    while (next - current.current < -180) next += 360;
    target.current = next;
    animate();
  }, [activeTool, tools, animate]);

  // Keyboard + button navigation (A11Y-1): select the previous/next tool.
  const selectByOffset = useCallback((delta: number) => {
    const i = tools.findIndex((tl) => tl.id === activeTool);
    const next = i < 0 ? (delta > 0 ? 0 : tools.length - 1) : (i + delta + tools.length) % tools.length;
    onToolSelect(tools[next].id);
  }, [tools, activeTool, onToolSelect]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); selectByOffset(1); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); selectByOffset(-1); }
    else if (e.key === "Home") { e.preventDefault(); onToolSelect(tools[0].id); }
    else if (e.key === "End") { e.preventDefault(); onToolSelect(tools[tools.length - 1].id); }
    else if ((e.key === "Enter" || e.key === " ") && !activeTool) { e.preventDefault(); onToolSelect(tools[0].id); }
    else if (e.key === "Escape" && activeTool) { e.preventDefault(); onDeselect(); }
  }, [selectByOffset, tools, activeTool, onToolSelect, onDeselect]);

  const navBtn: React.CSSProperties = {
    position: "absolute", top: 2, zIndex: 30, width: 32, height: 32, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
    background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))",
  };

  const step = 360 / tools.length;

  /**
   * The wheel's geometry is authored against a fixed 360-unit viewBox; only its
   * rendered size follows the panel. Scaling here rather than recomputing radii
   * keeps every path, label arc and font in proportion for free.
   *
   * On a phone the arc is width-bound, not height-bound: a circle can only get
   * as big as the narrowest side. So take the width that is actually there and
   * let the container height follow from it, instead of reserving a fixed 240px
   * that leaves dead space above the arc.
   */
  const [renderSize, setRenderSize] = useState(SIZE);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setRenderSize(Math.max(300, Math.min(460, w - 12)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Scale factor from the authored 360 units to the rendered pixels. */
  const k = renderSize / SIZE;

  return (
    <div
      ref={containerRef}
      className="donut-mobile-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-strong))]"
      aria-label="Tool selector: swipe, use the arrow buttons, or press arrow keys to rotate"
      role="listbox"
      tabIndex={0}
      onKeyDown={onKeyDown}
      style={{ touchAction: "pan-y", height: Math.round(renderSize * 0.5) + 34 }}
    >
      {/* Non-swipe affordance: previous / next tool */}
      <button type="button" aria-label="Previous tool" onClick={() => selectByOffset(-1)} style={{ ...navBtn, left: 8 }}>
        <svg width="8" height="12" viewBox="0 0 8 12"><path d="M7 1 L2 6 L7 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <button type="button" aria-label="Next tool" onClick={() => selectByOffset(1)} style={{ ...navBtn, right: 8 }}>
        <svg width="8" height="12" viewBox="0 0 8 12"><path d="M1 1 L6 6 L1 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {/* Swipe hint */}
      <div
        className="absolute z-20 pointer-events-none select-none"
        style={{
          top: 8, left: "50%", transform: "translateX(-50%)", opacity: 0.4,
          display: "flex", alignItems: "center", gap: 6,
          fontSize: 9, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
          color: "hsl(var(--muted-foreground))",
        }}
        aria-hidden="true"
      >
        <svg width="6" height="8" viewBox="0 0 6 8"><path d="M6 0 L0 4 L6 8z" fill="currentColor" /></svg>
        swipe
        <svg width="6" height="8" viewBox="0 0 6 8"><path d="M0 0 L6 4 L0 8z" fill="currentColor" /></svg>
      </div>

      {/* Donut positioned so the bottom half is clipped under the container edge */}
      <div
        className="relative"
        style={{ width: renderSize, height: renderSize, marginBottom: -Math.round(renderSize * 0.50) }}
      >
        <svg
          width={renderSize}
          height={renderSize}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ overflow: "visible", display: "block" }}
        >
          <g ref={ringRef}>
            {tools.map((tool, i) => {
              const a0 = i * step - 90 + GAP_DEG / 2;
              const a1 = (i + 1) * step - 90 - GAP_DEG / 2;
              const isActive = tool.id === activeTool;
              const arcId = `donut-m-lbl-${tool.id}`;
              const label = t(`tool.${tool.id}` as TranslationKey);

              return (
                <g
                  key={tool.id}
                  className="donut-slice-m"
                  role="option"
                  aria-selected={isActive}
                  aria-label={label}
                  onClick={() => onToolSelect(tool.id)}
                >
                  <path
                    d={sectorPath(OUTER_R, OUTER_R, INNER_R, OUTER_R, a0, a1)}
                    fill={BRAND}
                    fillOpacity={isActive ? 1 : 0.92}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    style={isActive ? { filter: "brightness(1.12)" } : undefined}
                  />
                  {isActive && (
                    <path
                      d={labelArcPath(OUTER_R, OUTER_R, OUTER_R - 3, a0, a1)}
                      fill="none"
                      stroke="rgba(255,255,255,0.85)"
                      strokeWidth={2}
                    />
                  )}
                  <path id={arcId} d={labelArcPath(OUTER_R, OUTER_R, LABEL_R, a0, a1)} fill="none" stroke="none" />
                  <text
                    className="donut-slice-label"
                    fill={isActive ? "white" : "hsl(var(--brand-fg))"}
                    fontSize={9}
                  >
                    <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">
                      {label}
                    </textPath>
                  </text>
                  {tool.badge && (() => {
                    const [bx, by] = polar(OUTER_R, OUTER_R, OUTER_R - 13, (a0 + a1) / 2);
                    return (
                      <g transform={`translate(${bx} ${by})`}>
                        <rect x={-12} y={-6.5} width={24} height={13} rx={6.5} fill="#fff" stroke="hsl(var(--brand))" strokeWidth={1} />
                        <text x={0} y={0} textAnchor="middle" dominantBaseline="central" fontSize={7} fontWeight={900} fill="hsl(var(--brand))">{tool.badge}</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}
          </g>
        </svg>

        {/* Center hole - brand wordmark + hint (HTML overlay over the visible top half) */}
        <div
          className="absolute rounded-full bg-background"
          style={{
            width: INNER_R * 2 * k,
            height: INNER_R * 2 * k,
            top: (OUTER_R - INNER_R) * k,
            left: (OUTER_R - INNER_R) * k,
            zIndex: 20,
            cursor: activeTool ? "pointer" : "default",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: 26 * k,
          }}
          onClick={activeTool ? onDeselect : undefined}
          title={activeTool ? "Tap to deselect" : undefined}
        >
          <div className="flex flex-col items-center" style={{ pointerEvents: "none" }}>
            <span
              style={{
                fontSize: 17 * k,
                fontWeight: 900,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                color: "hsl(var(--foreground))",
              }}
            >
              Shp<span style={{ color: BRAND }}>.Aid</span>
            </span>
            <span style={{ fontSize: 8, fontWeight: 700, color: "hsl(var(--muted-foreground))", letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 5 }}>
              {activeTool ? t("donut.reset") : t("donut.pick")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileDonut;
